import React from 'react'
import { UserExtraValidationSchemaEnum } from 'shared/entities-validator/user-fields-validators'
import { getSortedUserFields } from '../AuthHelpers'
import { useLoginToExistingAccountWithVerificationFlowMutation } from '../security-questions/SecurityQuestions.codegen'
import { useLoginContext } from '../login/LoginContext'
import { observer } from 'mobx-react'
import { AdditionalInfo } from './AdditionalInfo'
import { SelfSignupFormFieldsContextType } from '../self-signup/SelfSignupFormFieldsContext'
import { AskSecurityQuestionsAndLoginWithVerificationStep } from '../security-questions/AskSecurityQuestionsAndLoginWithVerificationStep'

const useBuildFields = (): SelfSignupFormFieldsContextType => {
  const ctx = useLoginContext()

  return React.useMemo((): SelfSignupFormFieldsContextType => {
    const privacyPolicyEnabled =
      ctx.organisation?.additionalFieldsConfig?.privacyPolicyEnabled

    const privacyPolicyUrl =
      (privacyPolicyEnabled &&
        ctx.organisation?.additionalFieldsConfig?.privacyPolicyUrl) ||
      null

    const privacyPolicyValidation = privacyPolicyEnabled
      ? [UserExtraValidationSchemaEnum.PRIVACY_POLICY]
      : []

    return {
      fields: [
        ...new Set([
          ...getSortedUserFields(ctx.additionalFieldsList),
          UserExtraValidationSchemaEnum.PASSWORD,
          UserExtraValidationSchemaEnum.PASSWORD_CONFIRMATION,
          ...privacyPolicyValidation
        ])
      ],
      privacyPolicyUrl,
      isAdditionalInfoForm: false
    }
  }, [ctx.organisation])
}

const useLoginToExistingAccountWithVerificationFlow = () => {
  const ctx = useLoginContext()

  const [fetch] = useLoginToExistingAccountWithVerificationFlowMutation()
  return async (): Promise<boolean> => {
    const { data } = await fetch({
      variables: {
        accountName: ctx.userIdentity,
        additionalData: {
          ...ctx.pickUserAdditionalData(),
          password: ctx.password
        },
        verificationData: ctx.verificationData!
      }
    })

    return !!data?.loginToExistingAccountWithVerificationFlow?.user
  }
}

export const AskAdditionalDataAndPasswordStep: React.FC = observer(() => {
  const ctx = useLoginContext()
  const fieldsContext = useBuildFields()
  const loginToExistingAccountWithVerificationFlow =
    useLoginToExistingAccountWithVerificationFlow()

  /**
   * returns true, if app should refetch Api session
   */
  const submit = async (): Promise<boolean> => {
    if (ctx.shouldAskSecurityQuestions()) {
      // clear & store only additional fields w/o privacy policy etc
      ctx.setAdditionalData(ctx.pickUserAdditionalData())
      ctx.setStepView(AskSecurityQuestionsAndLoginWithVerificationStep)

      return false
    }

    return loginToExistingAccountWithVerificationFlow()
  }

  return <AdditionalInfo submit={submit} fieldsContext={fieldsContext} />
})

AskAdditionalDataAndPasswordStep.displayName =
  'AskAdditionalDataAndPasswordStep'
